import { useMemo, useRef } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Dialog from '@mui/material/Dialog';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useNavigate } from 'react-router-dom';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { usePermissions } from '@/hooks/usePermissions';
import { useRecentItems } from '@/hooks/useRecentItems';
import { useWorkspaces } from '@/hooks/useWorkspaces';
import { collectLinkItems } from '@/navigation/sidebarNav';
import { ticketCreateHref } from '@/routes/paths';
import { useTenantScope } from '@/hooks/useTenantScope';

interface PaletteOption {
  id: string;
  group: string;
  label: string;
  href?: string;
}

const KIND_LABEL: Record<string, string> = {
  cliente: 'Cliente',
  ticket: 'Ticket',
  macchina: 'Macchina',
};

export interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Keyboard launcher: navigation from the sidebar, recent objects, quick actions.
 * No server search in this phase.
 */
export function CommandPalette({ open, onClose }: CommandPaletteProps): JSX.Element {
  const navigate = useNavigate();
  const { isDesktop } = useBreakpoint();
  const permissions = usePermissions();
  const { workspaces, sections } = useWorkspaces();
  const { items: recent } = useRecentItems();
  const tenantScope = useTenantScope();
  const previousFocus = useRef<HTMLElement | null>(null);

  if (open && previousFocus.current === null && document.activeElement instanceof HTMLElement) {
    previousFocus.current = document.activeElement;
  }

  const options = useMemo<PaletteOption[]>(() => {
    const placed = new Set<string>();
    const nav: PaletteOption[] = [];
    for (const workspace of workspaces) {
      const grouped = new Set(workspace.subsections.flatMap((subsection) => subsection.links.map((link) => link.id)));
      for (const subsection of workspace.subsections) {
        for (const link of subsection.links) {
          placed.add(link.id);
          nav.push({
            id: `nav:${link.id}`,
            group: `${workspace.label} · ${subsection.label}`,
            label: link.label,
            href: link.to,
          });
        }
      }
      for (const link of workspace.links) {
        if (grouped.has(link.id)) continue;
        placed.add(link.id);
        nav.push({
          id: `nav:${link.id}`,
          group: workspace.label,
          label: link.label,
          href: link.to,
        });
      }
    }
    for (const link of collectLinkItems(sections)) {
      if (placed.has(link.id)) continue;
      nav.push({
        id: `nav:${link.id}`,
        group: 'Account',
        label: link.label,
        href: link.to,
      });
    }

    const recentOptions = recent.map((item) => ({
      id: `recent:${item.kind}:${item.id}`,
      group: 'Recenti',
      label: `${KIND_LABEL[item.kind] ?? item.kind}: ${item.label}`,
      href: item.href,
    }));

    const actions: PaletteOption[] = [];
    if (permissions.canCreateTickets) {
      actions.push({
        id: 'action:new-ticket',
        group: 'Azioni rapide',
        label: 'Nuovo ticket',
        href: ticketCreateHref({ clienteId: permissions.isStaff ? tenantScope : undefined }),
      });
    }
    return [...nav, ...recentOptions, ...actions];
  }, [permissions, recent, tenantScope, workspaces, sections]);

  const handleClose = (): void => {
    onClose();
    const restore = previousFocus.current;
    previousFocus.current = null;
    window.setTimeout(() => restore?.focus(), 0);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      fullScreen={!isDesktop}
      maxWidth="sm"
      aria-label="Command palette"
      disableRestoreFocus
    >
      <Autocomplete
        options={options}
        groupBy={(option) => option.group}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        openOnFocus
        autoHighlight
        onChange={(_event, option) => {
          if (!option?.href) return;
          void navigate(option.href);
          handleClose();
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            autoFocus
            placeholder="Vai a una pagina, un recente o un’azione…"
            variant="outlined"
            sx={{ m: 2 }}
          />
        )}
        renderGroup={(params) => (
          <li key={params.key}>
            <Typography variant="caption" color="text.secondary" sx={{ px: 3, pt: 2, display: 'block' }}>
              {params.group}
            </Typography>
            <ul style={{ padding: 0, margin: 0 }}>{params.children}</ul>
          </li>
        )}
        noOptionsText="Nessun risultato"
        sx={{ pt: 1, pb: 2 }}
      />
    </Dialog>
  );
}
